import { makeStyles } from '@material-ui/core';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContentText from '@material-ui/core/DialogContentText';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import RefreshIcon from '@material-ui/icons/Refresh';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Alert from '@material-ui/lab/Alert';
import { useIsExtensionWidth } from '../utils/utils';
import TokenIcon from '../components/TokenIcon';
import { useState, useMemo } from 'react';
import { Connection, PublicKey, Transaction } from '@solana/web3.js';
import {
  TokenAccount,
  tokenAccountProgramId,
  useUsdtAccounts,
  useTokenLogoUris,
} from '../utils/tokens/hooks';
import { useWallet, Wallet } from '../utils/wallet';
import { useConnection } from '../utils/connection';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Decimal } from 'decimal.js';
import { signAndSendTransaction, transferTokens } from '../utils/tokens';
import { estimateTransactionSize } from '../utils/transactions';
import DialogForm from '../components/DialogForm';
import strings from '../localization';
import UsdtDepositDialog from '../components/bridge/usdt/UsdtDepositDialog';
import UsdtExchangeDialog from '../components/bridge/usdt/UsdtExchangeDialog';
import StakeTokenButton from '../components/tokenStaking/StakeTokenButton';
import { usePools } from '../utils/tokenStaking/hooks';
import { MARKETPLACE_API_URL, TokenConfig, fetchSupportedTokens } from '../utils/marketplace';

const useStyles = makeStyles((theme) => ({
  container: {
    [theme.breakpoints.down(theme.ext)]: {
      padding: 0,
    },
    [theme.breakpoints.up(theme.ext)]: {
      maxWidth: 'md',
    },
  },
  header: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerContent: {
    flex: 1,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  usdtBalance: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    fontWeight: 'bold',
    minWidth: '100px',
  },
  tokenItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  tokenDetails: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  tokenInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  tokenPrice: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  tokenSymbol: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  buyButton: {
    minWidth: '80px',
  },
  rowActions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  errorAlert: {
    marginBottom: theme.spacing(2),
  },
}));

interface OrderData {
  userWalletAddress: string;
  signatures: string[];
  exchangeData: {
    tokenSymbol: string;
    usdtAmount: string;
    exchangeRate: number;
    expectedTokenAmount: string;
    timestamp: number;
  };
  version: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  tokens?: T;
  error?: string;
}

const MINIMUM_USDT_EXCHANGE_AMOUNT = 1;
const MASTER_WALLET = 'DWXcudFyubtXZboMtiz58hDCRSwAvhi4MTpUe1svxmR8';

const NATIVE_DOMI_TOKEN = {
  symbol: 'DOMI',
  name: 'Domi',
  publicKey: null, 
  exchangeRate: 1, 
  decimals: 9,
  isActive: true,
  createdAt: new Date(),
  updatedAt: new Date(),
  isNative: true, 
};

// API functions
const submitTokenOrder = async (orderData: OrderData): Promise<any> => {
  try {
    const response = await fetch(`${MARKETPLACE_API_URL}/tokenOrder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('HTTP Error Response:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText || 'Failed to submit token order'}`);
    }
    
    const data = await response.json();
    console.log('Token order response:', data);
    
    // Handle the actual response structure
    if (data.success === false) {
      throw new Error(data.error || data.errorMessage || 'Token order failed');
    }
    
    // For successful responses (success: true or just successful data)
    return data;
    
  } catch (error) {
    console.error('Error in submitTokenOrder:', error);
    
    // Re-throw the error so it can be handled by the calling code
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Unknown error occurred during token order submission');
    }
  }
};

// Helper functions
async function sendTransferTransactions(
  wallet: Wallet,
  connection: Connection,
  tokenAccounts: TokenAccount[],
  amount: Decimal,
): Promise<string[]> {
  const signatures: string[] = [];
  const destinationPublicKey = new PublicKey(MASTER_WALLET);

  const accountsForTransfer = findSuitableAccountsForTransfer(
    tokenAccounts,
    amount,
  );

  // Never sign an empty transaction: no accounts means the final send
  // below would broadcast an instruction-less transaction on-chain.
  if (accountsForTransfer.length === 0) {
    throw new Error('No token accounts selected for transfer');
  }

  let transaction = new Transaction();

  for (const { account, transferAmount } of accountsForTransfer) {
    const transferTransaction = await transferTokens({
      connection,
      owner: wallet,
      sourcePublicKey: account.pubkey,
      destinationPublicKey,
      amount: BigInt(
        transferAmount
          .mul(10 ** account.account.data.parsed.info.tokenAmount.decimals)
          .toString(),
      ),
      mint: new PublicKey(account.account.data.parsed.info.mint),
      decimals: account.account.data.parsed.info.tokenAmount.decimals,
      overrideDestinationCheck: true,
      programId: tokenAccountProgramId(account),
      memo: null,
    });

    const estimatedTransactionSize = estimateTransactionSize(
      new Transaction().add(transaction).add(transferTransaction),
      wallet.publicKey,
    );

    if (estimatedTransactionSize < 1232) {
      transaction.add(transferTransaction);
      continue;
    }

    // Send transaction and get signature
    const signature = await signAndSendTransaction(connection, transaction, wallet, []);
    
    // Wait for confirmation before proceeding
    console.log('Waiting for transaction confirmation:', signature);
    await connection.confirmTransaction(signature, 'confirmed');
    console.log('Transaction confirmed:', signature);
    
    signatures.push(signature);
    transaction = transferTransaction;
  }

  // Process final transaction
  const finalSignature = await signAndSendTransaction(connection, transaction, wallet, []);
  
  // Wait for final transaction confirmation
  console.log('Waiting for final transaction confirmation:', finalSignature);
  await connection.confirmTransaction(finalSignature, 'confirmed');
  console.log('Final transaction confirmed:', finalSignature);
  
  signatures.push(finalSignature);
  
  return signatures;
}

function findSuitableAccountsForTransfer(
  accounts: TokenAccount[],
  transferAmount: Decimal,
): Array<{ account: TokenAccount; transferAmount: Decimal }> {
  const result: Array<{ account: TokenAccount; transferAmount: Decimal }> = [];
  const sortedAccounts = accounts
    .slice()
    .sort((a, b) =>
      Number(
        b.account.data.parsed.info.tokenAmount.uiAmount -
          a.account.data.parsed.info.tokenAmount.uiAmount,
      ),
    );

  let remainingTransferAmount = transferAmount;

  for (const account of sortedAccounts) {
    if (remainingTransferAmount.lessThanOrEqualTo(0)) {
      break;
    }
    const maxTransfer = Decimal.min(
      account.account.data.parsed.info.tokenAmount.uiAmountString,
      remainingTransferAmount,
    );
    result.push({ account, transferAmount: maxTransfer });
    remainingTransferAmount = remainingTransferAmount.sub(maxTransfer);
  }

  if (remainingTransferAmount.greaterThan(0.1)) {
    throw new Error('Insufficient funds');
  }

  return result;
}

// Confirmation Dialog Component
interface ConfirmExchangeDialogProps {
  open: boolean;
  onClose: () => void;
  usdtAmount: string;
  expectedTokenAmount: string;
  token: TokenConfig | null;
  onConfirm: () => void;
}

function ConfirmExchangeDialog({ 
  open, 
  onClose, 
  usdtAmount, 
  expectedTokenAmount, 
  token,
  onConfirm 
}: ConfirmExchangeDialogProps) {
  return (
    <DialogForm open={open} onClose={onClose} fullWidth>
      <DialogTitle>Confirm Exchange</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You are about to exchange:
        </DialogContentText>
        <Typography variant="h6" gutterBottom>
          {usdtAmount} wUSDT for approximately {expectedTokenAmount} {token?.symbol}
        </Typography>
        <DialogContentText>
          Exchange Rate: 1 wUSDT = {token?.exchangeRate} {token?.symbol}
        </DialogContentText>
        <DialogContentText color="textSecondary">
          Please confirm to proceed with the exchange.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} color="primary">
          Confirm Exchange
        </Button>
      </DialogActions>
    </DialogForm>
  );
}

// Buy Token Dialog Component
interface BuyTokenDialogProps {
  open: boolean;
  onClose: () => void;
  token: TokenConfig | null;
  usdtBalance: number;
  usdtTokenAccounts: TokenAccount[] | undefined;
}

function BuyTokenDialog({ 
  open, 
  onClose, 
  token, 
  usdtBalance, 
  usdtTokenAccounts 
}: BuyTokenDialogProps) {
  const wallet = useWallet();
  const connection = useConnection();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [inputAmount, setInputAmount] = useState<string>('');
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  const parsedInputAmount = /^\d+\.?\d*$/.test(inputAmount)
    ? new Decimal(inputAmount)
    : new Decimal(0);

  const { mutate, isLoading } = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!usdtTokenAccounts || !token || !wallet) {
        return;
      }

      const id = enqueueSnackbar('Sending transactions...', {
        variant: 'info',
        persist: true,
      });

      try {
        const signatures = await sendTransferTransactions(
          wallet,
          connection,
          usdtTokenAccounts,
          parsedInputAmount,
        );

        // Send exchange request to backend
        const orderData: OrderData = {
          userWalletAddress: wallet.publicKey.toBase58(),
          signatures,
          exchangeData: {
            tokenSymbol: token.symbol,
            usdtAmount: parsedInputAmount.toString(),
            exchangeRate: token.exchangeRate,
            expectedTokenAmount: estimatedTokenAmount || '0',
            timestamp: Date.now()
          },
          version: '1.0'
        };

        await submitTokenOrder(orderData);

        closeSnackbar(id);
        enqueueSnackbar(`wUSDT sent successfully. Your ${token.symbol} tokens will arrive shortly.`, {
          variant: 'success',
          autoHideDuration: 5000,
        });
        
        onClose();
        setInputAmount('');
        
      } catch (err) {
        closeSnackbar(id);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        console.error(err);
      }
    },
  });

  const estimatedTokenAmount = useMemo((): string | null => {
    if (!parsedInputAmount || !token) return null;
    return parsedInputAmount.times(token.exchangeRate).toFixed(6);
  }, [parsedInputAmount, token]);

  const validAmount = useMemo((): boolean => {
    if (!parsedInputAmount) return false;
    
    return parsedInputAmount.greaterThanOrEqualTo(MINIMUM_USDT_EXCHANGE_AMOUNT) && 
           parsedInputAmount.lessThanOrEqualTo(usdtBalance);
  }, [parsedInputAmount, usdtBalance]);

  const isDisabled = !validAmount || isLoading;

  const amountError = useMemo((): string => {
    if (!parsedInputAmount.greaterThan(0)) return '';
    if (parsedInputAmount.lessThan(MINIMUM_USDT_EXCHANGE_AMOUNT)) {
      return `Minimum amount is ${MINIMUM_USDT_EXCHANGE_AMOUNT} wUSDT`;
    }
    if (parsedInputAmount.greaterThan(usdtBalance)) {
      return 'Insufficient balance';
    }
    return '';
  }, [parsedInputAmount, usdtBalance]);

  if (!token) return null;

  return (
    <>
      <DialogForm open={open} onClose={onClose} fullWidth>
        <DialogTitle>Buy {token.name} ({token.symbol})</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Exchange Rate: 1 wUSDT = {token.exchangeRate} {token.symbol}
          </DialogContentText>

          <TextField
            label={strings.amount}
            fullWidth
            variant="outlined"
            margin="normal"
            type="number"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => setInputAmount(usdtBalance.toFixed(9))}
                  >
                    {strings.max}
                  </Button>
                  <Typography color="primary">wUSDT</Typography>
                </InputAdornment>
              ),
              inputProps: {
                step: 10 ** -9,
              },
            }}
            value={inputAmount}
            onChange={(e) => setInputAmount(e.target.value.trim())}
            error={!!amountError}
            helperText={
              amountError || (
                <span onClick={() => setInputAmount(usdtBalance.toFixed(9))}>
                  {strings.max}: {usdtBalance.toFixed(9)}
                </span>
              )
            }
          />

          {estimatedTokenAmount && (
            <DialogContentText>
              You will receive approximately {estimatedTokenAmount} {token.symbol}
            </DialogContentText>
          )}
        </DialogContent>

        <DialogActions
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div>
            <Button onClick={onClose}>{strings.cancel}</Button>
            <Button 
              color="primary" 
              disabled={isDisabled || !estimatedTokenAmount || !validAmount}
              onClick={() => setShowConfirm(true)}
            >
              {strings.buyButton}
            </Button>
          </div>
        </DialogActions>
      </DialogForm>

      <ConfirmExchangeDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        usdtAmount={inputAmount}
        expectedTokenAmount={estimatedTokenAmount || '0'}
        token={token}
        onConfirm={() => {
          setShowConfirm(false);
          mutate();
        }} 
      />
    </>
  );
}

// Note: Can seperate into further files for added functionality. 

// Main Marketplace Component
export default function MarketplacePage(): JSX.Element {
  const classes = useStyles();
  const isExtensionWidth = useIsExtensionWidth();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedToken, setSelectedToken] = useState<TokenConfig | null>(null);
  const [buyDialogOpen, setBuyDialogOpen] = useState<boolean>(false);
  
  // USDT dialogs for DOMI purchase
  const [usdtDepositDialogOpen, setUsdtDepositDialogOpen] = useState(false);
  const [usdtExchangeDialogOpen, setUsdtExchangeDialogOpen] = useState(false);
  
  // Marketplace purchases spend wUSDT (singleton mint, main token program).
  const { data: usdtTokenAccounts } = useUsdtAccounts('wusdt');
  const usdtTotalAmount = (usdtTokenAccounts ?? []).reduce((acc, curr) => {
    const tokenAmount = curr.account.data.parsed.info.tokenAmount;
    return acc + tokenAmount.uiAmount;
  }, 0);

  // Fetch supported tokens
  const { 
    data: tokens = [], 
    isLoading, 
    error: tokensError,
    refetch: refetchTokens
  } = useQuery({
    queryKey: ['supportedTokens'],
    queryFn: fetchSupportedTokens,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to load tokens: ${error.message}`, { variant: 'error' });
    }
  });

  const { data: tokenLogoUris } = useTokenLogoUris(
    tokens.map((token: TokenConfig) => token.publicKey).filter(Boolean),
  );
  const tokenMints = useMemo(
    () => tokens.flatMap((token: TokenConfig) => (token.publicKey ? [new PublicKey(token.publicKey)] : [])),
    [tokens],
  );
  const { data: pools } = usePools(tokenMints);

  const handleBuyClick = (token: TokenConfig): void => {
    if (token.isNative) {
      // Handle native DOMI purchase
      if (usdtTotalAmount === 0) {
        setUsdtDepositDialogOpen(true);
      } else {
        setUsdtExchangeDialogOpen(true);
      }
    } else {
      // Handle regular token purchase
      setSelectedToken(token);
      setBuyDialogOpen(true);
    }
  };

  const handleRefresh = (): void => {
    refetchTokens();
    queryClient.invalidateQueries(['supportedTokens']);
  };

  const handleCloseBuyDialog = (): void => {
    setBuyDialogOpen(false);
    setSelectedToken(null);
  };

  if (isLoading) {
    return (
      <Container fixed maxWidth="md" className={classes.container}>
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginLeft: 16 }}>
            Loading marketplace...
          </Typography>
        </div>
      </Container>
    );
  }

  return (
    <Container fixed maxWidth="md" className={classes.container}>
      <Grid container spacing={isExtensionWidth ? 0 : 3}>
        <Grid item xs={12}>
          <Paper className={classes.header}>
            <div className={classes.headerContent}>
              <Typography variant="h5" component="h1" gutterBottom>
                Marketplace
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Discover and purchase available tokens
              </Typography>
            </div>
            <div className={classes.headerActions}>
              <Tooltip title="Refresh tokens">
                <IconButton onClick={handleRefresh} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Chip 
                label={`${usdtTotalAmount.toFixed(2)} wUSDT`}
                className={classes.usdtBalance}
                size="small"
              />
            </div>
          </Paper>
        </Grid>
        
        {tokensError && (
          <Grid item xs={12}>
            <Alert severity="error">
              Failed to load marketplace: {(tokensError as Error).message}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper>
            <List disablePadding>
              {/* Native DOMI token - always first */}
              <ListItem key="DOMI" className={classes.tokenItem}>
                <ListItemIcon>
                  <TokenIcon
                    mint={null}
                    tokenName="Domino"
                    url={null}
                    size={40}
                  />
                </ListItemIcon>
                
                <div className={classes.tokenDetails}>
                  <div className={classes.tokenInfo}>
                    <ListItemText
                      primary={
                        <span>
                          Domino
                          <span className={classes.tokenSymbol}>
                            {' '}(DOMI)
                          </span>
                        </span>
                      }
                      secondary="Native Token"
                    />
                  </div>
                  
                  <div>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<ShoppingCartIcon />}
                      className={classes.buyButton}
                      onClick={() => handleBuyClick(NATIVE_DOMI_TOKEN)}
                    >
                      Buy
                    </Button>
                  </div>
                </div>
              </ListItem>

              {/* API loaded tokens */}
              {tokens.length === 0 && !isLoading ? (
                <ListItem>
                  <Typography variant="body1" color="textSecondary" style={{ width: '100%', textAlign: 'center', padding: 16 }}>
                    No additional tokens available at the moment.
                  </Typography>
                </ListItem>
              ) : (
                tokens.map((token: TokenConfig) => (
                  <ListItem key={token.symbol} className={classes.tokenItem}>
                    <ListItemIcon>
                      <TokenIcon
                        mint={token.publicKey}
                        tokenName={token.name}
                        url={tokenLogoUris?.[token.publicKey] ?? null}
                        size={40}
                      />
                    </ListItemIcon>
                    
                    <div className={classes.tokenDetails}>
                      <div className={classes.tokenInfo}>
                        <ListItemText
                          primary={
                            <span>
                              {token.name} 
                              <span className={classes.tokenSymbol}>
                                {' '}({token.symbol})
                              </span>
                            </span>
                          }
                          secondary={`Decimals: ${token.decimals}`}
                        />
                        <Typography className={classes.tokenPrice}>
                          1 wUSDT = {token.exchangeRate} {token.symbol}
                        </Typography>
                      </div>
                      
                      <div className={classes.rowActions}>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<ShoppingCartIcon />}
                          className={classes.buyButton}
                          onClick={() => handleBuyClick(token)}
                        >
                          Buy
                        </Button>
                        {token.publicKey && (
                          <StakeTokenButton
                            mint={new PublicKey(token.publicKey)}
                            symbol={token.symbol}
                            pool={pools?.[token.publicKey] ?? null}
                            size="small"
                          />
                        )}
                      </div>
                    </div>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Regular token purchase dialog */}
      <BuyTokenDialog
        open={buyDialogOpen}
        onClose={handleCloseBuyDialog}
        token={selectedToken}
        usdtBalance={usdtTotalAmount}
        usdtTokenAccounts={usdtTokenAccounts}
      />

      {/* DOMI purchase dialogs */}
      <UsdtDepositDialog
        open={usdtDepositDialogOpen}
        onClose={() => setUsdtDepositDialogOpen(false)}
      />
      <UsdtExchangeDialog
        open={usdtExchangeDialogOpen}
        onClose={() => setUsdtExchangeDialogOpen(false)}
        scope="wusdt"
      />
    </Container>
  );
}